import { z } from "zod";

/**
 * Sözleşme oluşturmaya UYGUNLUK — `GET /api/contracts/creation-eligibility`
 * (CLOS-101 `[062]`).
 *
 * ### Neden ayrı bir uç
 *
 * Kart iki şey istiyor: *"permission kontrolü yalnız istemci tarafında düğme gizlemeye
 * dayanmaz"* ve *"lisans, aktif sözleşme kapasitesi veya başka çalışma zamanı engeli varsa
 * neden açıkça gösterilir."* İkisi birlikte, düğmeye basıldığında SUNUCUYA sorulan bir
 * değerlendirme gerektiriyor: istemcinin elindeki yetki listesi bayat olabilir ve lisans
 * durumu istemcide hiç yok.
 *
 * ### Neden hata değil, sonuç
 *
 * Engel bir ARIZA değil, bir cevap: "bugün oluşturamazsın, sebebi şu." Hata zarfıyla
 * dönseydi arayüz onu bir aksaklık gibi sunar, kullanıcı da atacağı adımı öğrenemezdi.
 */

/**
 * Oluşturmayı engelleyen kanonik sebepler.
 *
 * ⚠ `capacity_reached` BİLEREK YOK. Aktif sözleşme kapasitesi sayacı `[068]` CLOS-198'in
 * işi ve bugün sayılacak sözleşme tablosu yok — hiçbir kodun döndüremeyeceği bir sebep
 * kodu, testin tutamayacağı bir söz olurdu. Kart da bunu izin veriyor: kapasite
 * *"koşulsuz UI hard blocker yapılmaz"* ve sonucu çalışma anında CLOS-198 üretir.
 */
export const CONTRACT_CREATION_BLOCKS = [
  "no_permission",
  "no_license",
  "capacity_reached",
  /**
   * DÖNEMSEL oluşturma hakkı tükendi (CLOS-836 · alt görev CLOS-973).
   *
   * ⚠ `capacity_reached`'ten AYRI SEBEP, çünkü kullanıcının atacağı adım farklı: aktif
   * kapasite dolduğunda bir sözleşme arşivlemek yeter, dönemsel hak tükendiğinde
   * arşivlemek HİÇBİR ŞEY değiştirmez — dönemin yenilenmesini beklemek ya da paket
   * yükseltmek gerekir. Tek koda indirseydik kullanıcıyı işe yaramayacak bir eyleme
   * yönlendirirdik.
   */
  "period_limit_reached",
] as const;
export type ContractCreationBlock = (typeof CONTRACT_CREATION_BLOCKS)[number];

/**
 * Engellerin kullanıcıya görünen açıklamaları.
 *
 * Metin BURADA, arayüzde değil (refleks 4). İki sebep AYIRT EDİCİ: kullanıcının atacağı
 * adım birinde "yöneticinden yetki iste", diğerinde "aboneliği düzelt". Tek bir
 * "oluşturamazsınız" mesajı ikisini de gizlerdi — `[037]` kapasite kartında aynı ayrım
 * bilinçli olarak yapılmıştı.
 */
export const CONTRACT_CREATION_BLOCK_NAMES: Record<ContractCreationBlock, string> = {
  no_permission: "Bu çalışma alanında sözleşme oluşturma yetkiniz yok.",
  no_license: "Çalışma alanınızın aboneliği sözleşme oluşturmaya izin vermiyor.",
  /**
   * ⚠ Lisanstan AYRI bir cümle (CLOS-198 `[068]` bağlandı). *"Aboneliğin izin vermiyor"*
   * ile *"kotan doldu"* aynı şey değil: birincide plan değişmeli, ikincide bir sözleşme
   * arşivlenmesi yeter. Tek cümle ikisini de gizler ve kullanıcıyı yanlış adıma iterdi.
   */
  capacity_reached: "Aktif sözleşme sınırına ulaşıldı. Yeni sözleşme için birini arşivleyin.",
  /**
   * ⚠ Arşivleme ÖNERİLMİYOR ve bu bilinçli: harcanmış dönemsel hak arşivlemeyle geri
   * gelmiyor. Aynı cümleyi kullansaydık kullanıcı sözleşme arşivler, hiçbir şey değişmez
   * ve sistemin bozuk olduğunu düşünürdü.
   */
  period_limit_reached:
    "Bu dönemde açabileceğiniz sözleşme sayısına ulaşıldı. Dönem yenilendiğinde hakkınız geri gelir.",
};

export const contractCreationEligibilitySchema = z.object({
  allowed: z.boolean(),
  /**
   * Engellerin TAMAMI; uygunsa boş dizi (CLOS-973).
   *
   * ⚠ Tek bir `block` alanıydı ve bu, iki kotanın birlikte dolduğu durumu ANLATAMIYORDU:
   * kullanıcı yalnız ilk sebebi görüyor, ötekini ancak onu çözdükten sonra öğreniyordu.
   * Kanonik kural iki kotayı ayrı sayıyor, dolayısıyla ekran da iki nedeni birden
   * gösterebilmeli.
   *
   * ⚠ SIRA anlamlı: yetki ve lisans engelleri kotalardan ÖNCE geliyor, kotalar arasında
   * da dönemsel hak önce — kullanıcıya söylenecek ilk şey, atması gereken ilk adım.
   */
  blocks: z.array(z.enum(CONTRACT_CREATION_BLOCKS)),
});
export type ContractCreationEligibility = z.infer<typeof contractCreationEligibilitySchema>;

/**
 * ### Sözleşme KAYDI (CLOS-107 `[069]`)
 *
 * ⚠ Bugün BİLEREK dar: kart *"boş sözleşme kaydı"* diyor. Taraflar (`[073]`), sorumlu ve
 * ekip (`[074]`), tarihler (`[075]`), yaşam döngüsü (`[076]`) ve metin (`[086]`+) kendi
 * kartlarıyla gelecek. Bugünden alan eklemek, hiçbir ekranın doldurmadığı zorunluluklar
 * yaratmak olurdu (ADR-0014).
 */

/**
 * Yaşam döngüsü DURUMLARI (CLOS-114 `[076]`).
 *
 * ⚠ On durumun beşi (`in_review` … `awaiting_signature`) bugün ULAŞILAMAZ ve bu bir eksik
 * değil, kartın kendi kuralı: *"workflow tarafından yönetilen duruma doğrudan kullanıcı
 * geçişi yapılamaz"* ve o workflow kartları (CLOS-155–167) henüz yok. Kart aynı cümlede
 * *"workflow kartı bulunmaması temel Taslak/Aktif/Sona Erdi/Feshedildi/Arşiv durum modelini
 * BLOKE ETMEZ"* diyor — yani kümenin tamamı tanımlanır, kolları workflow doldurur.
 *
 * Kümeyi bugünden bilmek gerekiyor çünkü bir sözleşmenin hangi durumlarda olabileceği
 * ürünün sabit bir gerçeği; hangi geçişin kimden yapılabildiği ise değişebilir bir kural
 * ve `config/contractLifecycle.json`'da.
 */
export const CONTRACT_STATUSES = [
  "draft",
  "in_review",
  "in_negotiation",
  "awaiting_approval",
  "agreed",
  "awaiting_signature",
  "active",
  "expired",
  "terminated",
  "archived",
] as const;
export type ContractStatus = (typeof CONTRACT_STATUSES)[number];

export const CONTRACT_STATUS_NAMES: Record<ContractStatus, string> = {
  draft: "Taslak",
  in_review: "İncelemede",
  in_negotiation: "Müzakerede",
  awaiting_approval: "Onay Bekliyor",
  agreed: "Mutabık",
  awaiting_signature: "İmza Bekliyor",
  active: "Aktif",
  expired: "Sona Erdi",
  terminated: "Feshedildi",
  archived: "Arşiv",
};

export const CONTRACT_TITLE_MAX = 200;

export const createContractSchema = z.object({
  title: z
    .string({ error: "Sözleşme başlığı zorunludur" })
    .trim()
    .min(1, "Sözleşme başlığı zorunludur")
    .max(CONTRACT_TITLE_MAX, `Başlık en fazla ${CONTRACT_TITLE_MAX} karakter olabilir`),
});
export type CreateContractInput = z.infer<typeof createContractSchema>;

export const contractSchema = z.object({
  id: z.uuid(),
  title: z.string(),
  status: z.enum(CONTRACT_STATUSES),
  createdBy: z.uuid(),
  createdAt: z.string(),
});
export type Contract = z.infer<typeof contractSchema>;

/**
 * Dosyadan sözleşme oluşturma girdisi (CLOS-108 `[071]`).
 *
 * ⚠ Dosyanın kendisi DEĞİL, güvenli dosya KİMLİĞİ alınıyor. Bayt akışını bu uca vermek,
 * `[005]`'in presign + tarama + karantina zincirini atlamak olurdu; kart *"kalıcılaştırma,
 * tenant izolasyonu, şifreleme, zararlı içerik taraması, karantina ve güvenli dosya kimliği
 * bu ortak altyapıdan alınır"* diyor. Yükleme önce oraya yapılıyor, sonra kimliği buraya
 * geliyor.
 */
export const createContractFromFileSchema = z.object({
  title: z
    .string({ error: "Sözleşme başlığı zorunludur" })
    .trim()
    .min(1, "Sözleşme başlığı zorunludur")
    .max(CONTRACT_TITLE_MAX, `Başlık en fazla ${CONTRACT_TITLE_MAX} karakter olabilir`),
  storageObjectId: z.uuid("Geçerli bir dosya kimliği değil"),
});
export type CreateContractFromFileInput = z.infer<typeof createContractFromFileSchema>;

/** Sözleşme + ilk versiyon: kart *"tek iş sonucu"* diyor, yanıt da öyle. */
export const contractWithVersionSchema = z.object({
  contract: contractSchema,
  version: z.object({
    id: z.uuid(),
    versionNumber: z.number().int().positive(),
    storageObjectId: z.uuid(),
    createdAt: z.string(),
  }),
});
export type ContractWithVersion = z.infer<typeof contractWithVersionSchema>;

/**
 * ŞABLONDAN SÖZLEŞME (CLOS-130 `[112]`).
 *
 * ⚠ Yalnız BAŞLIK: şablon kimliği yolda ve içerik şablondan geliyor. Gövdede içerik de
 * kabul etseydik, "şablondan oluşturuldu" diyen ama içeriği başka olan sözleşmeler
 * doğardı ve geçmiş yalan söylerdi.
 */
export const createContractFromTemplateSchema = z.object({
  title: z
    .string()
    .trim()
    .min(1, "Sözleşme başlığı zorunludur")
    .max(200, "Başlık en çok 200 karakter"),
  /** Şablondaki değişkenlerin değerleri (CLOS-132 `[113]`). */
  variables: z.record(z.string(), z.string().max(500)).optional(),
});
export type CreateContractFromTemplateInput = z.infer<typeof createContractFromTemplateSchema>;

/**
 * KULLANICIYA DÖNÜK SÜREÇ EVRELERİ (Shared Contract Detail §8).
 *
 * ### ⚠ Domain durumu ≠ ekran evresi
 *
 * On kanonik durum, karşı tarafa gösterilecek beş evreye eşleniyor. Domain enum'u UI
 * kolaylığı için KIRILMIYOR (rapor §8.2 ve "hard no" listesi): eşleme tek yönlü ve tek
 * yerde. İki ekran ayrı ayrı eşleseydi aynı sözleşme iki farklı aşamada görünürdü.
 *
 * ### ⚠ Bilinmeyen durum TAHMİN EDİLMİYOR
 *
 * Yeni bir durum eklenip burası güncellenmezse `null` dönüyor; ekran şeridi çizmiyor ve
 * yanlış bir evre uydurmuyor. "En yakın evreyi tahmin et" deseydik, ürün bir gün sessizce
 * yanlış bilgi gösterirdi.
 *
 * ### ⚠ Uç durumlar
 *
 * `expired`, `terminated`, `archived` şeridin son halkasına düşüyor: kullanıcı açısından
 * süreç bitmiştir. Ayrımı `CONTRACT_STATUS_NAMES` rozeti taşıyor.
 */
export const CONTRACT_DISPLAY_PHASES = [
  "hazirlik",
  "revizyon",
  "mutabakat",
  "imza",
  "tamamlandi",
] as const;
export type ContractDisplayPhase = (typeof CONTRACT_DISPLAY_PHASES)[number];

export const CONTRACT_DISPLAY_PHASE_NAMES: Record<ContractDisplayPhase, string> = {
  hazirlik: "Hazırlık",
  revizyon: "Revizyon",
  mutabakat: "Mutabakat",
  imza: "İmza",
  tamamlandi: "Tamamlandı",
};

const EVRE_ESLEMESI: Record<ContractStatus, ContractDisplayPhase> = {
  draft: "hazirlik",
  in_review: "hazirlik",
  in_negotiation: "revizyon",
  awaiting_approval: "mutabakat",
  agreed: "mutabakat",
  awaiting_signature: "imza",
  active: "tamamlandi",
  expired: "tamamlandi",
  terminated: "tamamlandi",
  archived: "tamamlandi",
};

/** Kanonik durum → ekran evresi. Bilinmeyen durumda `null` (tahmin yok). */
export function contractDisplayPhase(status: string): ContractDisplayPhase | null {
  return EVRE_ESLEMESI[status as ContractStatus] ?? null;
}
